import { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight, ClipboardCheck, FolderOpen } from 'lucide-react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ErrorState, LoadingState } from '../components/ui';
import { loadCourseTestFolderItems, loadCourseTestFolderTrail } from '../lib/data';
import { queryClass } from '../lib/parse';
import type { Course, TestInfo } from '../lib/types';

type FolderItem = { objectId: string; title: string };

export function StudentCourseTests() {
  const { courseId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const folderId = searchParams.get('folder') || undefined;
  const [course, setCourse] = useState<Course>();
  const [trail, setTrail] = useState<FolderItem[]>([]);
  const [folders, setFolders] = useState<TestInfo[]>([]);
  const [tests, setTests] = useState<TestInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const [courseRows, nextTrail, items] = await Promise.all([
          queryClass('elearning_Course', { limit: 1, where: (query) => query.equalTo('objectId', courseId) }) as Promise<Course[]>,
          loadCourseTestFolderTrail(courseId, folderId),
          loadCourseTestFolderItems(courseId, folderId),
        ]);
        if (!active) return;
        setCourse(courseRows.find((row) => !row.IsDeleted));
        setTrail(nextTrail);
        setFolders(items.folders);
        setTests(items.tests);
        setFailed(false);
      } catch {
        if (active) setFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [courseId, folderId]);

  const title = course?.CourseTitle || 'Course tests';
  const rootHref = `/courses/${courseId}/tests`;
  return <div className="course-tests-page">
    <Link className="back-link" to={`/courses/${courseId}`}><ArrowLeft size={17} /> {title}</Link>
    <header className="course-tests-heading"><span className="course-tests-icon"><ClipboardCheck size={22} /></span><div><h1>{title} tests</h1><p>Browse tests in this course by folder.</p></div></header>
    <nav className="test-folder-breadcrumb" aria-label="Test folder breadcrumb">
      <Link to={rootHref}>All folders</Link>
      {trail.map((item, index) => <span key={item.objectId} className="test-folder-crumb"><ChevronRight size={14} />{index === trail.length - 1 ? <strong aria-current="page">{item.title}</strong> : <Link to={`${rootHref}?folder=${encodeURIComponent(item.objectId)}`}>{item.title}</Link>}</span>)}
    </nav>
    {loading ? <LoadingState /> : failed ? <ErrorState onRetry={() => window.location.reload()} /> : <>
      {folders.length > 0 && <section className="course-test-section" aria-label="Test folders"><div className="subject-list">{folders.map((folder) => <Link className="subject-row course-test-row" key={folder.objectId} to={`${rootHref}?folder=${encodeURIComponent(folder.objectId)}`}><span><FolderOpen size={17} /></span><div><strong>{folder.TestTitle || 'Untitled folder'}</strong><small>Open folder</small></div><ChevronRight size={16} /></Link>)}</div></section>}
      {tests.length > 0 && <section className="course-test-section" aria-label="Published tests"><div className="subject-list">{tests.map((test) => <Link className="subject-row course-test-row" key={test.objectId} to={`/tests/${test.objectId}`} state={{ returnTo: `${location.pathname}${location.search}` }}><span><ClipboardCheck size={17} /></span><div><strong>{test.TestTitle || 'Untitled test'}</strong><small>{Number(test.NoOfQuestions || 0) || '—'} questions · {Number(test.Time || 0) || '—'} min · {Number(test.TotalMarks || 0) || '—'} marks</small></div><ChevronRight size={16} /></Link>)}</div></section>}
      {!folders.length && !tests.length && <div className="course-test-empty"><ClipboardCheck size={22} /><strong>No tests in this folder yet</strong><span>Published tests will appear here when they are added to this course.</span></div>}
    </>}
  </div>;
}
